import { useContext, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { FormControlLabel, Link, Typography } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
    CoreButton,
    CoreCard,
    CoreInfoIndicator,
    CoreModal,
    CoreSpinner,
    CoreTextField,
    coreToast
} from "@verity/components/core";
import { IntegrationSettingsActions } from "../../IntegrationSettingsActions";
import { IntegrationDetected } from "../../IntegrationDetected";
import { PortalControllerContext } from "../../../../context";
import apiClient from "../../../../../helpers/api-client";
import i18n from "../../../../../i18n";
import failImage from "../../../../../images/check-fail.svg";
import lti1p3IntegrationIcon from "../../../../../images/settings/lti1p3-integration.svg?react";
import DiscardIcon from "../../../../../images/portal/trash-simple.svg?react";
import styles from "./Lti1p3Integration.module.css";

type Deployment = {
    id: number;
    lti1p3ClientRegistrationId: number;
    clientId: string;
    deploymentId: string;
    iss: string;
    authorizationUri: string;
    jwkSetUri: string;
};

type EditableDeployment = Pick<Deployment, "clientId" | "deploymentId" | "iss" | "authorizationUri" | "jwkSetUri">;

type DeploymentFieldProps = {
    header: string;
    description: string;
    hint?: string;
    placeholder?: string;
    modified?: boolean;
    disabled?: boolean;
    value: string;
    setValue: (newValue: string) => void;
};

type DeploymentEditorProps = {
    deployment: EditableDeployment;
    modifiedFields: Set<keyof EditableDeployment>;
    lms: string;
    onFieldChange: (field: keyof EditableDeployment, value: string) => void;
    loading?: boolean;
};

const defaultEditableDeployment: EditableDeployment = {
    clientId: "",
    deploymentId: "",
    iss: "",
    authorizationUri: "",
    jwkSetUri: ""
};

const DeploymentField = ({ header, description, hint, placeholder, modified, disabled, value, setValue }: DeploymentFieldProps) => {
    const label = (
        <div className={styles.deploymentFieldLabel}>
            <Typography component="div" fontSize="15px" fontWeight="700" className={styles.deploymentFieldHeader}>
                {header}
                {hint && (
                    <div
                        className={styles.deploymentFieldHint}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <CoreInfoIndicator hint={hint} size={20} />
                    </div>
                )}
            </Typography>
            <Typography fontSize="15px">
                {description}
            </Typography>
        </div>
    );

    const helperText = (
        <span className={`${styles.helperText} ${modified && value === "" ? "" : styles.helperTextHidden}`}>
            <img className={styles.failImage} src={failImage} alt={"Fail"} />
            <Typography fontSize="14px">
                {i18n.t("portal.components.settings.lti1p3IntegrationTSX.required", { field: header })}
            </Typography>
        </span>
    );

    const control = (
        <div className={styles.deploymentFieldControl}>
            <CoreTextField
                style={{ width: "100%" }}
                value={value}
                placeholder={placeholder}
                helperText={helperText}
                disabled={disabled}
                onChange={(e) => setValue(e.target.value)}
            />
        </div>
    );

    return (
        <FormControlLabel
            labelPlacement="start"
            className={styles.deploymentField}
            label={label}
            control={control}
        />
    );
};

const DeploymentEditor = ({ deployment, modifiedFields, lms, onFieldChange, loading }: DeploymentEditorProps) => {
    return (
        <div className={styles.deploymentContainer}>
            <DeploymentField
                header={i18n.t("portal.components.settings.lti1p3IntegrationTSX.clientId.header")}
                description={i18n.t("portal.components.settings.lti1p3IntegrationTSX.clientId.description")}
                hint={i18n.t("portal.components.settings.lti1p3IntegrationTSX.clientId.hint", { lms })}
                placeholder={i18n.t("portal.components.settings.lti1p3IntegrationTSX.clientId.placeholder")}
                modified={modifiedFields.has("clientId")}
                disabled={loading}
                value={deployment.clientId}
                setValue={(value: string) => onFieldChange("clientId", value)}
            />
            <DeploymentField
                header={i18n.t("portal.components.settings.lti1p3IntegrationTSX.deploymentId.header")}
                description={i18n.t("portal.components.settings.lti1p3IntegrationTSX.deploymentId.description")}
                hint={i18n.t("portal.components.settings.lti1p3IntegrationTSX.deploymentId.hint", { lms })}
                placeholder={i18n.t("portal.components.settings.lti1p3IntegrationTSX.deploymentId.placeholder")}
                modified={modifiedFields.has("deploymentId")}
                disabled={loading}
                value={deployment.deploymentId}
                setValue={(value: string) => onFieldChange("deploymentId", value)}
            />
            <DeploymentField
                header={i18n.t("portal.components.settings.lti1p3IntegrationTSX.iss.header")}
                description={i18n.t("portal.components.settings.lti1p3IntegrationTSX.iss.description")}
                hint={i18n.t("portal.components.settings.lti1p3IntegrationTSX.iss.hint", { lms })}
                placeholder={i18n.t("portal.components.settings.lti1p3IntegrationTSX.iss.placeholder")}
                modified={modifiedFields.has("iss")}
                disabled={loading}
                value={deployment.iss}
                setValue={(value: string) => onFieldChange("iss", value)}
            />
            <DeploymentField
                header={i18n.t("portal.components.settings.lti1p3IntegrationTSX.oidc.header")}
                description={i18n.t("portal.components.settings.lti1p3IntegrationTSX.oidc.description")}
                hint={i18n.t("portal.components.settings.lti1p3IntegrationTSX.oidc.hint", { lms })}
                placeholder={i18n.t("portal.components.settings.lti1p3IntegrationTSX.oidc.placeholder")}
                modified={modifiedFields.has("authorizationUri")}
                disabled={loading}
                value={deployment.authorizationUri}
                setValue={(value: string) => onFieldChange("authorizationUri", value)}
            />
            <DeploymentField
                header={i18n.t("portal.components.settings.lti1p3IntegrationTSX.jwks.header")}
                description={i18n.t("portal.components.settings.lti1p3IntegrationTSX.jwks.description")}
                hint={i18n.t("portal.components.settings.lti1p3IntegrationTSX.jwks.hint", { lms })}
                placeholder={i18n.t("portal.components.settings.lti1p3IntegrationTSX.jwks.placeholder")}
                modified={modifiedFields.has("jwkSetUri")}
                disabled={loading}
                value={deployment.jwkSetUri}
                setValue={(value: string) => onFieldChange("jwkSetUri", value)}
            />
        </div>
    );
};

export const Lti1p3Integration = ({ instituteId, instituteUuid, lms }: { instituteId: number; instituteUuid: string; lms: string }) => {
    const { lti1p3Active } = useContext(PortalControllerContext);
    const [editableDeployment, setEditableDeployment] = useState<EditableDeployment>(defaultEditableDeployment);
    const [modifiedFields, setModifiedFields] = useState<Set<keyof EditableDeployment>>(new Set());
    const [activeDeploymentDatabaseId, setActiveDeploymentDatabaseId] = useState<number | undefined>(undefined);
    const [dataLoading, setDataLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removeModalOpen, setRemoveModalOpen] = useState(false);

    const edited = modifiedFields.size !== 0;
    const inputValid =
        editableDeployment.clientId &&
        editableDeployment.deploymentId &&
        editableDeployment.iss &&
        editableDeployment.authorizationUri &&
        editableDeployment.jwkSetUri;

    const handleFieldChange = (field: keyof EditableDeployment, value: string) => {
        setEditableDeployment((prev) => prev ? { ...prev, [field]: value } : defaultEditableDeployment);
        setModifiedFields((prev) => new Set([...prev, field]));
    };

    const onDiscard = () =>
        loadData(instituteId, instituteUuid)
            .then(() => coreToast.success(i18n.t("portal.components.settings.lti1p3IntegrationTSX.discardToastSuccess")))
            .catch(() => coreToast.error(i18n.t("portal.components.settings.lti1p3IntegrationTSX.discardToastError")));

    const onRemove = async () => {
        if (!activeDeploymentDatabaseId) return;
        setRemoveModalOpen(false);
        setSaving(true);

        try {
            // Doesn't reset the verity institute's issId field (see DeleteDeploymentModal in InstituteDeploymentsModal.tsx)
            await apiClient.admin.updateDeploymentAndIssuer(instituteId, {
                clientId: null,
                deploymentId: null
            });

            // Will delete the linked client registration if the deleted deployment was the only one using it
            await apiClient.admin.deleteDeployment(instituteUuid, activeDeploymentDatabaseId);

            loadData(instituteId, instituteUuid)
                .then(() => coreToast.success(i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeToastSuccess")));
        } catch (error) {
            console.error(`Failed to remove deployment${activeDeploymentDatabaseId ? ` ${activeDeploymentDatabaseId}` : ""}:`, error);
            coreToast.error(i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeToastError"));
        } finally {
            setSaving(false);
        }
    };

    const onSave = async () => {
        setSaving(true);
        
        try {
            let targetRegistrationId;

            if (activeDeploymentDatabaseId) {
                // Will update the current client registration if it's only used by this deployment, or will create a new one
                targetRegistrationId = await apiClient.admin.upsertDeploymentClientRegistration(
                    instituteUuid,
                    activeDeploymentDatabaseId,
                    {
                        clientId: editableDeployment.clientId,
                        iss: editableDeployment.iss,
                        authorizationUri: editableDeployment.authorizationUri,
                        jwkSetUri: editableDeployment.jwkSetUri
                    }
                );

                await apiClient.admin.updateDeployment(activeDeploymentDatabaseId, instituteUuid, {
                    deploymentId: editableDeployment.deploymentId,
                    lti1p3ClientRegistrationId: targetRegistrationId
                });
            } else {
                try {
                    const clientRegistrationResponse = await apiClient.admin.getClientRegistrationFromClientIdAndIss(
                        editableDeployment.clientId,
                        editableDeployment.iss
                    );
                    targetRegistrationId = clientRegistrationResponse?.clientRegistrations?.[0]?.id;
                } catch (error: any) {
                    if (error?.status !== 404) throw error;
                }

                if (!targetRegistrationId) {
                    targetRegistrationId = (await apiClient.admin.createClientRegistration(instituteUuid, {
                        clientId: editableDeployment.clientId,
                        iss: editableDeployment.iss,
                        authorizationUri: editableDeployment.authorizationUri,
                        jwkSetUri: editableDeployment.jwkSetUri
                    }))?.id;
                }
                
                await apiClient.admin.createDeployment(instituteUuid, {
                    deploymentId: editableDeployment.deploymentId,
                    lti1p3ClientRegistrationId: targetRegistrationId
                });
            }

            // Updates the deployment fields for the verity institute in lti_1p3_institute; either links to an existing, matching issuer from lti_1p3_issuer or creates a new one
            await apiClient.admin.updateDeploymentAndIssuer(instituteId, {
                clientId: editableDeployment.clientId,
                deploymentId: editableDeployment.deploymentId,
                iss: editableDeployment.iss,
                authorizationUri: editableDeployment.authorizationUri,
                jwkSetUri: editableDeployment.jwkSetUri
            }).then(
                () => coreToast.success(i18n.t("portal.components.settings.lti1p3IntegrationTSX.saveToastSuccess"))
            );

            loadData(instituteId, instituteUuid);
        } catch (error) {
            console.error("Failed to save deployment settings:", error);
            coreToast.error(i18n.t("portal.components.settings.lti1p3IntegrationTSX.saveToastError"));
        } finally {
            setSaving(false);
        }
    };
    
    const loadData = async (instituteId: number, instituteUuid: string) => {
        setEditableDeployment(defaultEditableDeployment);
        setActiveDeploymentDatabaseId(undefined);
        setModifiedFields(new Set());
        setDataLoading(true);
        
        try {
            const activeDeployment = await apiClient.admin.getActiveDeployment({ instituteId, instituteUuid });
            if (activeDeployment) {
                setActiveDeploymentDatabaseId(activeDeployment.id);
                setEditableDeployment({
                    clientId: activeDeployment.clientId,
                    deploymentId: activeDeployment.deploymentId,
                    iss: activeDeployment.iss,
                    authorizationUri: activeDeployment.authorizationUri,
                    jwkSetUri: activeDeployment.jwkSetUri
                });
            }
        } catch (error) {
            console.error("Failed to fetch active deployment", error);
        } finally {
            setDataLoading(false);
        }
    };

    useEffect(() => {
        void loadData(instituteId, instituteUuid);
    }, [instituteId, instituteUuid]);

    let platformName = "";
    let guideLink = "";
    
    if (lms === "canvas") {
        platformName = "Canvas";
        guideLink = "https://support.yuja.com/hc/en-us/articles/7332584857623-Integrating-Verity-for-Test-Proctoring-into-Canvas";
    } else if (lms === "desire2learn") {
        platformName = "D2L";
        guideLink = "https://support.yuja.com/hc/en-us/articles/12752624794007-Integrating-Verity-for-Test-Proctoring-into-D2L-Brightspace";
    } else if (lms === "moodle") {
        platformName = "Moodle";
        guideLink = "https://support.yuja.com/hc/en-us/articles/8225791775511-Integrating-YuJa-Verity-for-Test-Proctoring-into-Moodle";
    } else if (lms === "BlackboardLearn") {
        platformName = "Blackboard";
        guideLink = "https://support.yuja.com/hc/en-us/articles/11910738375191-Integrating-YuJa-Verity-for-Test-Proctoring-into-Blackboard-Learn-Ultra";
    }

    if (!platformName || !guideLink) {
        return <div>{i18n.t("portal.components.settings.lti1p3IntegrationTSX.lmsUnsupported")}</div>
    }

    return (
        <div>
            <IntegrationSettingsActions
                actions={[
                    {
                        text: i18n.t("portal.components.settings.lti1p3IntegrationTSX.discardChanges"),
                        icon: <DiscardIcon width="22px" />,
                        color: "cancel",
                        disabled: saving || dataLoading || !edited,
                        onClick: onDiscard
                    },
                    {
                        text: i18n.t("portal.components.settings.lti1p3IntegrationTSX.saveChanges"),
                        icon: saving ? <CoreSpinner size="16px" /> : <SaveOutlinedIcon width="22px" />,
                        disabled: saving || dataLoading || !edited || !inputValid,
                        onClick: onSave
                    }
                ]}
            />
            <CoreCard
                useHeader
                title={i18n.t("portal.components.settings.lti1p3IntegrationTSX.title")}
                rightTitleContent={<IntegrationDetected detected={lti1p3Active} integration={i18n.t("portal.components.settings.lti1p3IntegrationTSX.ltiLaunch")} />}
                icon={lti1p3IntegrationIcon}
                className={styles.card}
            >
                <div className={styles.description}>
                    <Trans
                        i18n={i18n}
                        i18nKey="portal.components.settings.lti1p3IntegrationTSX.description"
                        values={{ platformName }}
                        components={{ a: <Link href={guideLink} target="_blank" rel="noopener noreferrer" /> }}
                    />
                </div>
                <DeploymentEditor
                    deployment={editableDeployment}
                    modifiedFields={modifiedFields}
                    lms={platformName}
                    onFieldChange={handleFieldChange}
                    loading={dataLoading}
                />
                <div className={styles.removeContainer}>
                    <CoreButton
                        color="cancel"
                        disabled={!activeDeploymentDatabaseId}
                        onClick={() => setRemoveModalOpen(true)}
                    >
                        {i18n.t("portal.components.settings.lti1p3IntegrationTSX.remove")}
                    </CoreButton>
                </div>
            </CoreCard>
            <CoreModal
                open={removeModalOpen}
                title={i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeModal.title")}
                maxWidth="600px"
                onClose={() => setRemoveModalOpen(false)}
                buttons={[
                    {
                        text: i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeModal.cancel"),
                        color: "secondary",
                        onClick: () => setRemoveModalOpen(false)
                    },
                    {
                        text: i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeModal.remove"),
                        color: "cancel",
                        onClick: onRemove
                    }
                ]}
            >
                <Typography>{i18n.t("portal.components.settings.lti1p3IntegrationTSX.removeModal.body")}</Typography>
            </CoreModal>
        </div>
    );
};
